import React, { useEffect, useMemo, useState } from 'react';
import {
  Upload,
  Film,
  Sparkles,
  CheckCircle2,
  Loader,
  AlertCircle,
  Eye,
  X,
  Play,
  Copy,
  ExternalLink,
  Image as ImageIcon,
  RefreshCw,
  Plus,
} from 'lucide-react';
import {
  uploadAsset,
  producePromoFromScript,
  listProjects,
  getProject,
  resolveAssets,
  listScenes,
  type UploadAssetResult,
  type AVProject,
  type AVScene,
  type ProducePromoResult,
} from '@/modules/av-producer/api';

interface Props {
  addNotification?: (n: { type: string; title: string; message?: string }) => void;
}

// Spec por default — Sevilla Palace Mundial (producción cinematográfica
// "millones de dólares": México vivo, animación 2D sobre live action — quetzal,
// papel picado, mariachi neon — integrada con la arquitectura del hotel.
// Voz en off + música de fondo (pásala en audio.background_music_url).
// reference_assets (global): asset_keys de fotos del hotel ya subidas; sus
// descripciones de Vision se inyectan como STYLE REFERENCE en cada prompt.
const DEFAULT_SPEC = `{
  "project": "Sevilla Palace · Mundial 2026",
  "style": "cinematic luxury, mexicanidad contemporánea, hybrid 2D animation over live action, magical realism, neon mariachi palette, papel picado motion graphics, golden hour to neon night, anamorphic lens flares, dolby vision color grade, anthemic build",
  "aspect_ratio": "16:9",
  "voice": {"language": "es"},
  "audio": {
    "background_music_url": "",
    "background_music_volume": 0.18,
    "voiceover_volume": 1.0
  },
  "reference_assets": [],
  "scenes": [
    {
      "id": 1, "duration_sec": 5,
      "initial_frame": "hotel_exterior_day_or_golden_hour.jpg",
      "final_frame": "hotel_exterior_zoomed_in.jpg",
      "prompt": "luxury hotel facade in Mexico City CDMX golden hour, sun bouncing off glass, an animated 2D quetzal bird (vibrant emerald green tail-feathers, magical realism, Studio Ghibli-meets-Pixar quality) flies in front of the camera leaving a trail of soft neon particles, papel picado banners flutter at the edges of the frame, anamorphic lens flares, cinematic, IMAX scale, ultra-sharp",
      "camera_motion": "slow push-in following the quetzal as it ascends past the hotel facade",
      "transition_out": "feather_wipe",
      "text_overlay": {"text": "Cada cuatro años, México despierta."},
      "audio_hint": "soft mariachi violin opens, deep cinematic boom on push-in"
    },
    {
      "id": 2, "duration_sec": 5,
      "initial_frame": "hotel_lobby_or_chandelier.jpg",
      "final_frame": "football_ball_floating_in_lobby.jpg",
      "prompt": "interior of a luxury hotel lobby in CDMX, a glowing football ball floats mid-air reflecting the chandelier and marble, holographic constellation lines connect the ball to the ceiling architecture, animated 2D papel picado overlays in Mexican pink, turquoise and gold drift like petals, slow-motion, dramatic god-rays through tall windows, cinematic depth of field",
      "camera_motion": "orbit around the floating ball, hotel architecture sweeps behind",
      "transition_out": "light_bloom",
      "text_overlay": {"text": "Y la pasión vive entre nuestros muros."},
      "audio_hint": "deep sub-bass impact, choral 'oohs', mariachi trumpet fanfare swells"
    },
    {
      "id": 3, "duration_sec": 5,
      "initial_frame": "hotel_room.jpg",
      "final_frame": "hotel_room_with_light_changes.jpg",
      "prompt": "interior of a luxury suite at Sevilla Palace, animated 2D mariachi silhouettes (sombrero, charro suit) cast as warm shadows on the wall playing trumpets, neon mexican folk-art motifs glow softly, the TV reflects a stadium roar, papel picado floats lazily, warm amber and deep indigo lighting, cinematic, magical realism",
      "camera_motion": "smooth pan left across the suite, ending on the window",
      "transition_out": "crossfade",
      "text_overlay": {"text": "Cada habitación, un palco al partido del siglo."},
      "audio_hint": "guitarrón + crowd murmur, heartbeat sub"
    },
    {
      "id": 4, "duration_sec": 5,
      "initial_frame": "rooftop_view_day.jpg",
      "final_frame": "rooftop_view_night_city_lights.jpg",
      "prompt": "Mexico City skyline from the Sevilla Palace rooftop pool, day-to-night hyperlapse, the city lights ignite like stadium floodlights one by one, an animated 2D quetzal silhouette flies across the skyline scattering neon particles, animated 2D papel picado banners stretched across the sky like a stadium tifo, fireworks burst in cempasúchil orange and turquoise, anamorphic flares, cinematic IMAX",
      "camera_motion": "slow tilt up + dolly back, revealing the full skyline",
      "transition_out": "stadium_light_pulse",
      "text_overlay": {"text": "México será sede. Tú, protagonista."},
      "audio_hint": "anthemic build, taiko drums, 'olé olé' choral chant entering"
    },
    {
      "id": 5, "duration_sec": 5,
      "initial_frame": "football_ball_clean.jpg",
      "final_frame": "ball_dissolving_into_eagle.jpg",
      "prompt": "the glowing football ball flies down a hotel corridor in slow-motion, particles trailing, it hits a wall mural and dissolves into an animated 2D crystalline aztec eagle (Mexican coat-of-arms reimagined as Pixar-quality magical realism) which spreads its wings filling the frame in golden light, cinematic, mythic, IMAX, dolby vision",
      "camera_motion": "push forward fast, then crash zoom into the eagle's chest",
      "transition_out": "white_flash",
      "text_overlay": {"text": "No es un juego: es identidad."},
      "audio_hint": "war drum hit, mariachi trumpet stinger, choir crescendo"
    },
    {
      "id": 6, "duration_sec": 4,
      "initial_frame": "hotel_logo_or_best_image.jpg",
      "final_frame": "hotel_logo_static.jpg",
      "prompt": "Sevilla Palace logo reveal on a clean dark backdrop, animated 2D quetzal circles the logo leaving a neon emerald trail, animated papel picado banners drape softly above and below, golden particles drift, elegant cinematic reveal, premium luxury brand finish, dolby vision",
      "camera_motion": "slight zoom in, holding on the locked logo",
      "transition_out": "fade_out",
      "text_overlay": {"text": "Sevilla Palace · Aquí se vive el Mundial."},
      "audio_hint": "final mariachi trumpet flourish + sub-bass resolve"
    }
  ]
}`;

function normalizeKey(k: string): string {
  let key = (k || '').trim();
  if (!key) return '';
  key = key.split('/').pop() || key;
  if (key.includes('.')) key = key.split('.').slice(0, -1).join('.') || key;
  return key;
}

const ReferenceQuickAdd: React.FC<{ onAdd: (name: string) => void }> = ({ onAdd }) => {
  const [val, setVal] = useState('');
  const submit = () => {
    if (!val.trim()) return;
    onAdd(val);
    setVal('');
  };
  return (
    <div className="flex items-center gap-2">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        placeholder="ej. hotel_lobby_main"
        className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1 text-white/90 focus:outline-none focus:ring-1 focus:ring-fuchsia-400/40 w-44"
      />
      <button
        onClick={submit}
        className="px-2 py-1 text-xs bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 rounded inline-flex items-center gap-1"
      >
        <Plus className="h-3 w-3" /> añadir
      </button>
    </div>
  );
};

export const AVProducerPage: React.FC<Props> = ({ addNotification }) => {
  const [specText, setSpecText] = useState(DEFAULT_SPEC);
  const [parseError, setParseError] = useState('');
  const [uploadStatus, setUploadStatus] = useState<Record<string, UploadAssetResult>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [projectId, setProjectId] = useState(0);
  const [producing, setProducing] = useState(false);
  const [produceResult, setProduceResult] = useState<ProducePromoResult | null>(null);
  const [projectStatus, setProjectStatus] = useState<AVProject | null>(null);
  const [projectScenes, setProjectScenes] = useState<AVScene[]>([]);
  const [productionStartedAt, setProductionStartedAt] = useState<number>(0);
  const [recentProjects, setRecentProjects] = useState<AVProject[]>([]);

  const spec = useMemo(() => {
    try {
      const parsed = JSON.parse(specText);
      setParseError('');
      return parsed;
    } catch (e: any) {
      setParseError(e.message || 'JSON inválido');
      return null;
    }
  }, [specText]);

  // Asset keys únicos del spec (para drop zones)
  const assetKeys = useMemo(() => {
    if (!spec?.scenes) return [];
    const keys = new Set<string>();
    for (const s of spec.scenes) {
      const f = normalizeKey(s.initial_frame);
      const l = normalizeKey(s.final_frame);
      if (f) keys.add(f);
      if (l) keys.add(l);
    }
    return Array.from(keys);
  }, [spec]);

  // Asset keys de referencia visual (estilo, NO frames). Sus descripciones
  // de Vision se inyectan como STYLE REFERENCE prefix en cada prompt.
  const referenceKeys = useMemo(() => {
    if (!spec?.reference_assets || !Array.isArray(spec.reference_assets)) return [] as string[];
    return spec.reference_assets
      .map((k: any) => normalizeKey(typeof k === 'string' ? k : ''))
      .filter(Boolean);
  }, [spec]);

  const allKnownKeys = useMemo(() => {
    const s = new Set([...assetKeys, ...referenceKeys]);
    return Array.from(s);
  }, [assetKeys, referenceKeys]);

  const allUploaded = assetKeys.length > 0 && assetKeys.every((k) => uploadStatus[k]);

  // Cargar proyectos recientes
  const loadProjects = async () => {
    try {
      const projects = await listProjects();
      setRecentProjects(projects.slice(0, 5));
    } catch (e: any) {
      // OK si falla — modulo aún funciona
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  // Pre-resolver: ¿qué asset_keys (frames + referencias) ya existen en Media?
  // Si existen, NO pedir upload otra vez — pre-popular el slot.
  useEffect(() => {
    if (allKnownKeys.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const matches = await resolveAssets(allKnownKeys);
        if (cancelled) return;
        setUploadStatus((prev) => {
          const next = { ...prev };
          for (const m of matches) {
            if (m.found && !next[m.asset_key]) {
              const key = (m.asset_key || '').replace(/\.[^.]+$/, '').split('/').pop() || m.asset_key;
              next[key] = {
                media_id: m.media_id,
                asset_key: key,
                url: m.url,
                alt_text: m.alt_text,
                vision_description: m.ai_description_short,
              };
            }
          }
          return next;
        });
      } catch {
        /* silencioso — fallback a upload manual */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allKnownKeys.join('|')]);

  // Polling del proyecto + scenes si está in_production
  useEffect(() => {
    if (!projectId || !producing) return;
    let timer: any;
    const poll = async () => {
      try {
        const [p, scenes] = await Promise.all([
          getProject(projectId),
          listScenes(projectId).catch(() => [] as AVScene[]),
        ]);
        setProjectStatus(p);
        setProjectScenes(scenes);
        if (p.status === 'done' || p.status === 'failed' || p.status === 'review' || p.status === 'draft') {
          setProducing(false);
          loadProjects();
        } else {
          timer = setTimeout(poll, 6000);
        }
      } catch {
        timer = setTimeout(poll, 10000);
      }
    };
    timer = setTimeout(poll, 3000);
    return () => clearTimeout(timer);
  }, [projectId, producing]);

  const handleFileSelected = async (key: string, file: File) => {
    setUploading((s) => ({ ...s, [key]: true }));
    try {
      const result = await uploadAsset(file, key, projectId || undefined);
      setUploadStatus((s) => ({ ...s, [key]: result }));
      addNotification?.({
        type: 'success',
        title: `${key} subida`,
        message: 'Vision corre async, revisa la descripción en ~5s',
      });
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: `Error subiendo ${key}`,
        message: e?.message || 'unknown',
      });
    } finally {
      setUploading((s) => ({ ...s, [key]: false }));
    }
  };

  const handleDrop = (key: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(key, file);
  };

  const handleProduce = async (forceKeys: string[] = []) => {
    if (!spec) return;
    setProducing(true);
    if (forceKeys.length === 0) setProduceResult(null);
    if (forceKeys.length === 0) setProjectScenes([]);
    setProductionStartedAt(Date.now());
    try {
      const r = await producePromoFromScript(spec, {
        project_id: projectId,
        sync: false,
        force_regenerate_scene_keys: forceKeys,
      });
      setProduceResult(r);
      setProjectId(r.project_id);
      addNotification?.({
        type: 'success',
        title: forceKeys.length ? `Re-render disparado: ${forceKeys.join(', ')}` : 'Producción iniciada',
        message: `AVProject ${r.project_id}. Workers procesan ~3-5 min.`,
      });
    } catch (e: any) {
      setProducing(false);
      addNotification?.({
        type: 'error',
        title: 'Error iniciando producción',
        message: e?.message,
      });
    }
  };

  // Añadir asset_key a spec.reference_assets (mutación del JSON visible)
  const addReferenceAsset = (rawName: string) => {
    const k = normalizeKey(rawName);
    if (!k) return;
    try {
      const parsed = JSON.parse(specText);
      const refs = Array.isArray(parsed.reference_assets) ? parsed.reference_assets.slice() : [];
      if (refs.some((x: any) => normalizeKey(x) === k)) return;
      refs.push(k);
      parsed.reference_assets = refs;
      setSpecText(JSON.stringify(parsed, null, 2));
    } catch {
      addNotification?.({
        type: 'error',
        title: 'JSON inválido',
        message: 'No pude parsear el spec para añadir la referencia',
      });
    }
  };

  const removeReferenceAsset = (key: string) => {
    try {
      const parsed = JSON.parse(specText);
      const refs = Array.isArray(parsed.reference_assets) ? parsed.reference_assets : [];
      parsed.reference_assets = refs.filter((x: any) => normalizeKey(x) !== normalizeKey(key));
      setSpecText(JSON.stringify(parsed, null, 2));
    } catch {
      /* noop */
    }
  };

  const finalUrl = projectStatus?.distribution?.final_video_url || produceResult?.final_video_url;
  const sceneClipUrls = (projectStatus?.distribution?.scene_clip_urls as string[]) || produceResult?.scene_clip_urls || [];
  const costBreakdown =
    (projectStatus?.distribution?.cost_breakdown as any) ||
    produceResult?.cost_breakdown ||
    null;
  const fmtUSD = (n: number) =>
    typeof n === 'number' && isFinite(n) ? `$${n.toFixed(n < 0.1 ? 4 : 2)}` : '$0';
  const sceneCostMap = useMemo(() => {
    const m: Record<string, { image: number; video: number; voice: number }> = {};
    const arr = costBreakdown?.per_scene || [];
    for (const r of arr) m[r.scene_key] = { image: r.image, video: r.video, voice: r.voice };
    return m;
  }, [costBreakdown]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Film className="h-6 w-6 text-[var(--acc1)]" />
              Productor Audiovisual · Orión
            </h2>
            <p className="text-white/60 text-sm mt-1">
              Sube fotos por escena → vision describe → Kling v2 Pro anima → ffmpeg concat → video final
            </p>
          </div>
          {projectId > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-white/60">
                AVProject <code className="text-[var(--acc1)]">#{projectId}</code> · {projectStatus?.status || 'pending'}
              </span>
              {costBreakdown?.totals && (
                <span
                  className="text-xs px-2 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                  title={`gpt-image-1: ${fmtUSD(costBreakdown.totals.image)} · Kling: ${fmtUSD(costBreakdown.totals.video)} · TTS: ${fmtUSD(costBreakdown.totals.voice)}`}
                >
                  💸 {fmtUSD(costBreakdown.totals.total_usd)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Spec editor */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> JSON del script
          </h3>
          <span className="text-xs text-white/50">
            {assetKeys.length} asset_keys únicos · {spec?.scenes?.length || 0} escenas
          </span>
        </div>
        <textarea
          value={specText}
          onChange={(e) => setSpecText(e.target.value)}
          rows={10}
          className="w-full font-mono text-xs bg-black/40 border border-white/10 text-white/90 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-[var(--acc1)]/50"
        />
        {parseError && (
          <div className="mt-2 text-rose-300 text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {parseError}
          </div>
        )}
      </div>

      {/* Imágenes de referencia (estilo) — STYLE REFERENCE prefix en cada prompt */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <ImageIcon className="h-4 w-4" /> Imágenes de referencia (estilo)
            </h3>
            <p className="text-white/50 text-xs mt-1">
              Fotos del hotel/lugar que viajan como referencia visual a CADA escena (paleta, materiales, arquitectura).
              Su descripción de Vision se inyecta como STYLE REFERENCE en los prompts.
            </p>
          </div>
          <ReferenceQuickAdd onAdd={addReferenceAsset} />
        </div>
        {referenceKeys.length === 0 ? (
          <div className="text-white/40 text-xs italic">
            Sin referencias. Añade nombres como <code>hotel_lobby_main</code>,{' '}
            <code>fachada_dia</code>, <code>suite_signature</code> y luego sube las fotos.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {referenceKeys.map((key) => {
              const status = uploadStatus[key];
              const isUploading = uploading[key];
              return (
                <div
                  key={`ref-${key}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop(key)}
                  className={`relative border-2 border-dashed rounded-lg p-3 text-xs transition ${
                    status
                      ? 'border-fuchsia-500/40 bg-fuchsia-500/10'
                      : isUploading
                      ? 'border-amber-500/40 bg-amber-500/10'
                      : 'border-white/15 bg-white/5 hover:border-fuchsia-400/40'
                  }`}
                >
                  <button
                    onClick={() => removeReferenceAsset(key)}
                    className="absolute top-1 right-1 p-1 rounded hover:bg-white/10"
                    title="Quitar de referencias"
                  >
                    <X className="h-3 w-3 text-white/60" />
                  </button>
                  <div className="flex items-start justify-between gap-2 mb-2 pr-5">
                    <code className="text-[10px] text-fuchsia-200/90 break-all">{key}</code>
                    {status ? (
                      <CheckCircle2 className="h-4 w-4 text-fuchsia-300 flex-shrink-0" />
                    ) : isUploading ? (
                      <Loader className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" />
                    ) : null}
                  </div>
                  {status ? (
                    <div className="space-y-1">
                      <img src={status.url} alt={key} className="w-full h-20 object-cover rounded border border-white/10" />
                      {status.vision_description && !status.vision_description.startsWith('(en proceso') && (
                        <div className="text-white/40 text-[9px] line-clamp-2 italic">
                          {status.vision_description}
                        </div>
                      )}
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelected(key, file);
                        }}
                      />
                      <div className="h-20 flex items-center justify-center text-white/40 text-[10px] text-center">
                        Arrastra foto<br />o click
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Drop zones por asset key */}
      {assetKeys.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Upload className="h-4 w-4" /> Frames por escena ({Object.keys(uploadStatus).filter(k => assetKeys.includes(k)).length}/{assetKeys.length})
            </h3>
            <span className="text-xs text-white/50">drag-drop o click para seleccionar</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {assetKeys.map((key) => {
              const status = uploadStatus[key];
              const isUploading = uploading[key];
              return (
                <div
                  key={key}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop(key)}
                  className={`border-2 border-dashed rounded-lg p-3 text-xs transition ${
                    status
                      ? 'border-emerald-500/40 bg-emerald-500/10'
                      : isUploading
                      ? 'border-amber-500/40 bg-amber-500/10'
                      : 'border-white/15 bg-white/5 hover:border-[var(--acc1)]/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <code className="text-[10px] text-white/80 break-all">{key}</code>
                    {status ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    ) : isUploading ? (
                      <Loader className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" />
                    ) : null}
                  </div>
                  {status ? (
                    <div className="space-y-1">
                      <img src={status.url} alt={key} className="w-full h-24 object-cover rounded border border-white/10" />
                      <div className="text-white/50 text-[10px] flex items-center gap-1 justify-between">
                        <span className="inline-flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          #{status.media_id}
                        </span>
                        <span className="text-emerald-300/80">
                          {status.vision_description?.startsWith('(en proceso')
                            ? 'recién subida'
                            : 'ya en biblioteca ♻'}
                        </span>
                      </div>
                      {status.vision_description &&
                        !status.vision_description.startsWith('(en proceso') && (
                          <div className="text-white/40 text-[9px] line-clamp-2 italic">
                            {status.vision_description}
                          </div>
                        )}
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelected(key, file);
                        }}
                      />
                      <div className="h-24 flex items-center justify-center text-white/40 text-[10px] text-center">
                        Arrastra imagen<br />o click para seleccionar
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Producir */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Play className="h-4 w-4" /> Producir video
            </h3>
            <p className="text-white/60 text-xs mt-1">
              Si NO subes alguna foto, esa scene la genera con gpt-image-1 (fallback).
              Recomendado: subir todas las fotos reales primero.
            </p>
          </div>
          <button
            onClick={handleProduce}
            disabled={!spec || producing}
            className="px-6 py-2.5 bg-[var(--acc1)] hover:bg-[var(--acc1)]/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
          >
            {producing ? <Loader className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {producing
              ? 'Produciendo…'
              : allUploaded
              ? 'Producir video ahora'
              : Object.keys(uploadStatus).length === 0
              ? 'Producir (100% AI fallback)'
              : 'Producir (con fallbacks)'}
          </button>
        </div>
      </div>

      {/* Progreso por scene (si hay scenes cargadas) */}
      {projectScenes.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Loader className={`h-4 w-4 ${producing ? 'animate-spin text-amber-300' : 'text-white/50'}`} />
              Progreso por escena #{projectId}
            </h3>
            {(() => {
              const done = projectScenes.filter((s) => s.status === 'done').length;
              const generating = projectScenes.filter((s) => s.status === 'generating').length;
              const failed = projectScenes.filter((s) => s.status === 'failed').length;
              const pending = projectScenes.filter((s) => s.status === 'pending').length;
              const total = projectScenes.length;
              const elapsed = productionStartedAt ? Math.floor((Date.now() - productionStartedAt) / 1000) : 0;
              const avgPerScene = done > 0 && elapsed > 0 ? elapsed / done : 90;
              const remainingScenes = generating + pending;
              const eta = remainingScenes > 0 ? Math.ceil(remainingScenes * avgPerScene) : 0;
              return (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-emerald-300">✓ {done}</span>
                  {generating > 0 && <span className="text-amber-300">⟳ {generating}</span>}
                  {pending > 0 && <span className="text-white/60">⏳ {pending}</span>}
                  {failed > 0 && <span className="text-rose-300">✗ {failed}</span>}
                  <span className="text-white/40">/ {total}</span>
                  {producing && eta > 0 && (
                    <span className="text-white/60">ETA ~{Math.floor(eta / 60)}m {eta % 60}s</span>
                  )}
                  {producing && elapsed > 0 && (
                    <span className="text-white/40">· {Math.floor(elapsed / 60)}m {elapsed % 60}s</span>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="space-y-2">
            {projectScenes.map((s) => {
              const err = (s.cua_annotations || {}).error || '';
              const clipUrl = (s.audio_config || {}).clip_url || '';
              const prompt = (s.visual || {}).prompt || '';
              const motion = (s.visual || {}).camera_motion || '';
              const stateColor = {
                done: 'border-emerald-500/30 bg-emerald-500/10',
                generating: 'border-amber-500/30 bg-amber-500/10',
                failed: 'border-rose-500/30 bg-rose-500/10',
                pending: 'border-white/10 bg-white/5',
              }[s.status] || 'border-white/10 bg-white/5';
              const stateIcon = {
                done: <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />,
                generating: <Loader className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" />,
                failed: <AlertCircle className="h-4 w-4 text-rose-400 flex-shrink-0" />,
                pending: <Loader className="h-4 w-4 text-white/30 flex-shrink-0" />,
              }[s.status] || null;
              return (
                <div key={s.id} className={`border rounded-lg p-3 ${stateColor}`}>
                  <div className="flex items-start gap-3">
                    {stateIcon}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-white font-medium text-sm">
                          {s.scene_key} <span className="text-white/40 font-normal">· {s.duration_sec}s</span>
                        </span>
                        <span className="text-[10px] uppercase tracking-wide text-white/60">{s.status}</span>
                      </div>
                      {prompt && (
                        <div className="text-white/60 text-xs mt-1 line-clamp-2 italic">
                          {prompt}
                          {motion && <span className="text-white/40"> · cámara: {motion}</span>}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        {clipUrl && (
                          <a
                            href={clipUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[var(--acc1)] text-xs hover:underline"
                          >
                            <Play className="h-3 w-3" /> ver clip
                          </a>
                        )}
                        {(() => {
                          const c = sceneCostMap[s.scene_key];
                          if (!c) return null;
                          const total = (c.image || 0) + (c.video || 0) + (c.voice || 0);
                          if (total <= 0) return null;
                          return (
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-400/30 text-emerald-200/90"
                              title={`img ${fmtUSD(c.image)} · video ${fmtUSD(c.video)} · voz ${fmtUSD(c.voice)}`}
                            >
                              💸 {fmtUSD(total)}
                            </span>
                          );
                        })()}
                        {(s.status === 'done' || s.status === 'failed') && projectId > 0 && (
                          <button
                            onClick={() => handleProduce([s.scene_key])}
                            disabled={producing}
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-fuchsia-500/15 hover:bg-fuchsia-500/30 text-fuchsia-200 disabled:opacity-40"
                            title="Re-renderear solo esta escena (mantiene el resto)"
                          >
                            <RefreshCw className="h-3 w-3" /> re-renderear esta escena
                          </button>
                        )}
                      </div>
                      {err && (
                        <div className="text-rose-300 text-xs mt-1 break-all bg-rose-500/10 p-1.5 rounded">
                          {err.length > 200 ? err.slice(0, 200) + '…' : err}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resultado */}
      {(projectStatus || produceResult) && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Estado del proyecto #{projectId}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="text-white/70">
              Status: <span className="text-[var(--acc1)] font-medium">{projectStatus?.status || produceResult?.status}</span>
            </div>
            {costBreakdown?.totals && (
              <div className="border border-emerald-400/20 bg-emerald-500/5 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-white/70 text-xs uppercase tracking-wide">💸 Costo total ({costBreakdown.currency || 'USD'})</span>
                  <span className="text-emerald-300 font-semibold text-base">{fmtUSD(costBreakdown.totals.total_usd)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px] text-white/60">
                  <div>Imágenes (gpt-image-1): <span className="text-white/80">{fmtUSD(costBreakdown.totals.image)}</span></div>
                  <div>Video (Kling v2.1): <span className="text-white/80">{fmtUSD(costBreakdown.totals.video)}</span></div>
                  <div>Voz (TTS): <span className="text-white/80">{fmtUSD(costBreakdown.totals.voice)}</span></div>
                </div>
                <div className="text-white/40 text-[10px] italic">
                  Estimación. Edge TTS = $0; ElevenLabs cobrado por chars; reuso de Media existente = $0.
                </div>
              </div>
            )}
            {finalUrl && (
              <div className="space-y-2 mt-3">
                <video src={finalUrl} controls className="w-full max-w-3xl rounded border border-white/10" />
                <div className="flex gap-2 items-center">
                  <code className="text-xs text-[var(--acc1)] break-all">{finalUrl}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(finalUrl)}
                    className="p-1 hover:bg-white/10 rounded"
                    title="Copy URL"
                  >
                    <Copy className="h-4 w-4 text-white/70" />
                  </button>
                  <a
                    href={finalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1 hover:bg-white/10 rounded"
                    title="Open"
                  >
                    <ExternalLink className="h-4 w-4 text-white/70" />
                  </a>
                </div>
              </div>
            )}
            {sceneClipUrls.length > 0 && (
              <details className="mt-3">
                <summary className="text-white/60 text-xs cursor-pointer">{sceneClipUrls.length} clips por scene (Kling raw)</summary>
                <div className="mt-2 space-y-1">
                  {sceneClipUrls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" className="block text-xs text-[var(--acc1)] truncate hover:underline">
                      Scene {i + 1}: {u.slice(0, 80)}…
                    </a>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Recientes */}
      {recentProjects.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">Proyectos recientes</h3>
          <div className="space-y-2">
            {recentProjects.map((p) => {
              const url = (p.distribution || {}).final_video_url || '';
              return (
                <button
                  key={p.id}
                  onClick={async () => {
                    setProjectId(p.id);
                    setProduceResult(null);
                    try {
                      const [detail, scenes] = await Promise.all([
                        getProject(p.id),
                        listScenes(p.id).catch(() => [] as AVScene[]),
                      ]);
                      setProjectStatus(detail);
                      setProjectScenes(scenes);
                    } catch {
                      /* silencioso */
                    }
                  }}
                  className="w-full text-left flex items-center justify-between gap-3 p-2 rounded hover:bg-white/5"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/40">#{p.id}</span>
                    <span className="text-white/80 text-sm">{p.title}</span>
                    {url && (
                      <span className="text-[10px] text-emerald-300/80">▶ video listo</span>
                    )}
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-white/60">
                    {p.status}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
